import APITemplate from '@api/APITemplate'
import store from '@state/store'
import con from '@services/connection'
import { formatQueryParams } from '@utils/Format'
import {
  CREDITOS_SALDO,
  CREDITOS_PACOTES,
  CREDITOS_CHECKOUT,
  CREDITOS_PAGAMENTO,
  CREDITOS_EXTRATO,
  CREDITOS_RESUMO,
} from '@modules/ai-framework/constants/URL'

const ERRO_INESPERADO =
  'Não foi possível carregar o saldo de créditos. Tente novamente mais tarde ou contate o suporte.'
const ERRO_PACOTES =
  'Não foi possível carregar os pacotes de créditos. Tente novamente mais tarde ou contate o suporte.'
const ERRO_CHECKOUT =
  'Não foi possível iniciar o pagamento. Tente novamente mais tarde ou contate o suporte.'
const ERRO_PAGAMENTO =
  'Não foi possível consultar o pagamento. Tente novamente mais tarde ou contate o suporte.'
const ERRO_EXTRATO =
  'Não foi possível carregar o extrato de créditos. Tente novamente mais tarde ou contate o suporte.'
const ERRO_RESUMO =
  'Não foi possível carregar o resumo de créditos. Tente novamente mais tarde ou contate o suporte.'
// Para o envelope que chega SEM `message` — a resposta nula já vira as mensagens acima.
const ERRO_PACOTES_SEM_MENSAGEM =
  'Não foi possível carregar os pacotes de créditos. Tente novamente.'
const ERRO_EXTRATO_SEM_MENSAGEM =
  'Não foi possível carregar o extrato de créditos. Tente novamente.'
const ERRO_RESUMO_SEM_MENSAGEM =
  'Não foi possível carregar o resumo do período. Tente novamente.'

const STATUS_OK = 200
// Estado de TELA (copy própria no bloco), não erro de operação — sem snack.
const STATUS_FORBIDDEN = 403
// Resposta AUTORITATIVA do pagamento: não existe, ou é de outro tenant — a mesma para os
// dois (P1), sem snack, como o `forbidden` do saldo.
const STATUS_NOT_FOUND = 404

// `location='json'` no reqparse do checkout (`resource_creditos_checkout.py:145-148`): sem o
// opt-in o `connection.js` manda FormData e os dois campos chegariam como STRING — e o
// `null` de um deles viraria a string "null", que o backend leria como campo PREENCHIDO e
// recusaria com "exatamente um". Mesmo opt-in dos outros writes do módulo
// (`apiAiFrameworkInstances`, `apiAiFrameworkApprovals`).
const ENVIAR_JSON = { json: true }

// Nomes para os argumentos posicionais de `con.get` — mesmo padrão de
// `apiAtendimentoConexao.js` (`getComPrazo`): a assinatura é `(url, data, type, ignoreErro,
// options)`, e no call-site isso viraria `null, 'get', false`, que não diz nada a quem lê.
const DATA_VAZIO = null
const TIPO_GET = 'get'
const NAO_IGNORAR_ERRO = false
// ★ S16 (review PR #2211, re-QA qa2-005): sem prazo, uma leitura travada prendia
// `pagamentoLoading` PARA SEMPRE — o guard de concorrência do polling descartava todo
// tick seguinte, `tentativas` parava de andar, e o botão "Atualizar" do teto ficava
// desabilitado com spinner. 8s é folga generosa para uma leitura de status e ainda bem
// menor que os 60s do orçamento normal de tentativas — ao estourar, `connection.js` monta
// um envelope 408 e `lerPagamento` trata como o mesmo erro transitório de um 500.
const TIMEOUT_PAGAMENTO_MS = 8000
const COM_PRAZO_PAGAMENTO = { timeout: TIMEOUT_PAGAMENTO_MS }

// Carteira de créditos (project-ia-payment). A leitura do saldo é a spec-001; a COMPRA
// (catálogo, checkout na Stripe e estado de um pagamento) é a spec-002; o extrato e o
// resumo são a spec-003. O plano recorrente (spec-005) mora em `apiAiFrameworkAssinatura`.
// Nada nesta classe muta saldo: quem credita é o webhook da Stripe, no servidor — o
// cliente só lê e abre a cobrança.
//
// Molde do gerador (`_templates/new/api/api.ejs.t`): cada método faz o request E persiste
// o resultado na store pelos setters de `store/creditos.js` — a action Vuex nunca busca.
// Envelope { status, message, value } de response.data — o caller lê `status` do BODY,
// nunca o HTTP (o backend responde 200 inclusive em 400/403/404/500/502, contrato AAWZ).
//
// ★ Duas disciplinas de concorrência, e elas não se misturam:
// - LEITURAS versionadas (saldo, pacotes, extrato, resumo): todo pedido sai, e quem é
//   descartada é a RESPOSTA obsoleta — sucesso OU falha, porque o ramo de falha limpa o
//   dado da tela e uma resposta velha que falha apagaria o que a mais nova confirmou.
// - ESCRITA/POLLING com guard BLOQUEANTE (checkout, pagamento): o segundo pedido não sai.
//   Descartar a resposta não desfaz uma cobrança criada na Stripe.
class APIAiFrameworkCreditos extends APITemplate {
  constructor() {
    super()

    this.MODULE = 'aiFramework/creditos'

    // STORE KEYS
    this.KEY_CHECKOUT_LOADING = `${this.MODULE}/checkoutLoading`
    this.KEY_PAGAMENTO = `${this.MODULE}/pagamento`
    this.KEY_PAGAMENTO_LOADING = `${this.MODULE}/pagamentoLoading`
    this.KEY_SALDO_RECARREGADO_PARA = `${this.MODULE}/saldoRecarregadoPara`
    this.KEY_EXTRATO_FILTERS = `${this.MODULE}/extratoFilters`
    this.KEY_EXTRATO_PAGE = `${this.MODULE}/extratoPage`
    this.KEY_RESUMO_FILTERS = `${this.MODULE}/resumoFilters`

    // STORE ACTIONS — num `Object.assign` só: uma linha por action levaria o constructor
    // além do `max-statements` do repo.
    Object.assign(this, {
      ACTION_SET_SALDO: `${this.MODULE}/setSaldo`,
      ACTION_SET_SALDO_LOADING: `${this.MODULE}/setSaldoLoading`,
      ACTION_SET_SALDO_FORBIDDEN: `${this.MODULE}/setSaldoForbidden`,
      ACTION_SET_PACOTES: `${this.MODULE}/setPacotes`,
      ACTION_SET_PACOTES_LOADING: `${this.MODULE}/setPacotesLoading`,
      ACTION_SET_PACOTES_ERRO: `${this.MODULE}/setPacotesErro`,
      ACTION_SET_CHECKOUT: `${this.MODULE}/setCheckout`,
      ACTION_SET_CHECKOUT_LOADING: `${this.MODULE}/setCheckoutLoading`,
      ACTION_SET_CHECKOUT_ERRO: `${this.MODULE}/setCheckoutErro`,
      ACTION_SET_PAGAMENTO: `${this.MODULE}/setPagamento`,
      ACTION_SET_PAGAMENTO_LOADING: `${this.MODULE}/setPagamentoLoading`,
      ACTION_SET_PAGAMENTO_NAO_ENCONTRADO: `${this.MODULE}/setPagamentoNaoEncontrado`,
      ACTION_SET_SALDO_RECARREGADO_PARA: `${this.MODULE}/setSaldoRecarregadoPara`,
      ACTION_SET_EXTRATO: `${this.MODULE}/setExtrato`,
      ACTION_SET_EXTRATO_TOTAL: `${this.MODULE}/setExtratoTotal`,
      ACTION_SET_EXTRATO_FILTERS: `${this.MODULE}/setExtratoFilters`,
      ACTION_SET_EXTRATO_PAGE: `${this.MODULE}/setExtratoPage`,
      ACTION_SET_EXTRATO_LOADING: `${this.MODULE}/setExtratoLoading`,
      ACTION_SET_EXTRATO_FORBIDDEN: `${this.MODULE}/setExtratoForbidden`,
      ACTION_SET_EXTRATO_ERRO: `${this.MODULE}/setExtratoErro`,
      ACTION_SET_EXTRATO_REGUA: `${this.MODULE}/setExtratoRegua`,
      ACTION_SET_RESUMO: `${this.MODULE}/setResumo`,
      ACTION_SET_RESUMO_FILTERS: `${this.MODULE}/setResumoFilters`,
      ACTION_SET_RESUMO_LOADING: `${this.MODULE}/setResumoLoading`,
      ACTION_SET_RESUMO_FORBIDDEN: `${this.MODULE}/setResumoForbidden`,
      ACTION_SET_RESUMO_ERRO: `${this.MODULE}/setResumoErro`,
      ACTION_SNACK: 'feedback/showSnack',
    })

    // Número de ordem do último disparo de cada leitura (versionamento). É estado de
    // REQUISIÇÃO, e por isso vive na instância e não no escopo do módulo (review #2211
    // S17): no escopo do módulo, dois stores com o mesmo slice tornariam obsoleta a
    // resposta um do outro.
    this.saldoSeq = 0
    this.pacotesSeq = 0
    this.extratoSeq = 0
    this.resumoSeq = 0
  }

  // GET /creditos/saldo — saldo do profissional, lido e PERSISTIDO em `saldo*`.
  // `creditos` volta como STRING de 6 casas (Decimal serializado): converter para número
  // aqui perderia precisão da trilha financeira. O arredondamento é SÓ de exibição (ADR-007).
  // `idProfissional` é QUERY PARAM OPCIONAL: só o gestor consulta outro profissional; de
  // não-gestor o backend ignora e responde sobre a própria carteira — nunca vaza saldo alheio.
  //
  // ★ Dois disparos em voo (created + refresh, dois cards na tela) fazem duas requisições,
  // e as duas SAEM — a que o cliente não pode perder é a mais nova: é ela que traz o saldo
  // depois de um pagamento creditado. Só o pedido corrente aplica o resultado e encerra o
  // loading; nem o snack de uma falha obsoleta entra na tela.
  async carregarSaldo({ idProfissional = null } = {}) {
    const seq = ++this.saldoSeq
    store.dispatch(this.ACTION_SET_SALDO_LOADING, true)

    try {
      const params = formatQueryParams({ id_profissional: idProfissional })
      const r =
        (await con.get(CREDITOS_SALDO + params)) ||
        this.buildErrorResponse({ message: ERRO_INESPERADO, value: null })
      if (seq === this.saldoSeq) this.aplicarSaldoLido(r)
      return r
    } finally {
      if (seq === this.saldoSeq) store.dispatch(this.ACTION_SET_SALDO_LOADING, false)
    }
  }

  // GET /creditos/pacotes — catálogo vigente (spec-002 [US-1]), persistido em `pacotes*`.
  // Sem query param nenhum: a vigência é resolvida no servidor. `creditos_base`,
  // `creditos_bonus` e `creditos_totais` chegam como STRING de 6 casas e são repassados
  // intactos: o total é somado no SERVIDOR.
  async carregarPacotes() {
    const seq = ++this.pacotesSeq
    store.dispatch(this.ACTION_SET_PACOTES_LOADING, true)

    try {
      const r =
        (await con.get(CREDITOS_PACOTES)) ||
        this.buildErrorResponse({ message: ERRO_PACOTES, value: null })
      if (seq === this.pacotesSeq) this.aplicarPacotesLido(r)
      return r
    } finally {
      if (seq === this.pacotesSeq) store.dispatch(this.ACTION_SET_PACOTES_LOADING, false)
    }
  }

  // POST /creditos/checkout — cria a intenção de compra e a sessão na Stripe ([US-2]).
  // Devolve o envelope: é por ele que a view decide navegar para `url_checkout`, e nunca
  // navega em erro. ★ NADA é creditado aqui: o checkout só abre a cobrança.
  //
  // ★ Guard BLOQUEANTE que vale DINHEIRO: dois cliques em "Confirmar" abririam duas sessões
  // na Stripe e dois pagamentos para a mesma intenção. `finally` solta a flag mesmo se a
  // rede lançar — presa, o guard recusaria toda compra até o F5.
  abrirCheckout({ codigoPacote = null, valorCentavos = null } = {}) {
    if (store.getters[this.KEY_CHECKOUT_LOADING]) return
    store.dispatch(this.ACTION_SET_CHECKOUT_LOADING, true)
    const soltar = () => store.dispatch(this.ACTION_SET_CHECKOUT_LOADING, false)
    return this.postarCheckout({ codigoPacote, valorCentavos }).finally(soltar)
  }

  // ★ O corpo carrega SÓ o campo que o usuário escolheu — preço, créditos e bônus são
  // relidos da tabela pelo `codigo_pacote`. Campo não informado é OMITIDO, não enviado
  // como `null`: "exatamente um dos dois" é a regra do servidor, e um `null` que o
  // transporte transformasse em string faria o backend ver os DOIS campos e recusar uma
  // compra legítima. Quando os dois vêm preenchidos, os dois são enviados — quem recusa é
  // o servidor.
  async postarCheckout({ codigoPacote, valorCentavos }) {
    const payload = {}
    if (codigoPacote !== null && codigoPacote !== undefined) {
      payload.codigo_pacote = codigoPacote
    }
    if (valorCentavos !== null && valorCentavos !== undefined) {
      // Inteiro de CENTAVOS ponta a ponta — dinheiro em float é proibido no projeto.
      payload.valor_centavos = valorCentavos
    }

    const r =
      (await con.post(CREDITOS_CHECKOUT, payload, false, ENVIAR_JSON)) ||
      this.buildErrorResponse({ message: ERRO_CHECKOUT, value: null })
    this.aplicarCheckoutCriado(r)
    return r
  }

  // GET /creditos/pagamentos/:id_pagamento — estado de UM pagamento ([US-2], [US-3],
  // [US-5]), alvo do polling da tela de retorno. Sem id não sai request nem muda o loading.
  //
  // Guard de concorrência: o polling AUTOMÁTICO não pode empilhar requisições quando uma
  // resposta demora mais que o intervalo — o tick sobreposto recebe `undefined`, e o
  // componente não o conta como tentativa.
  //
  // ★ `manual` ignora o guard de propósito (S16 follow-up, achado do QA): o teto do polling
  // pode chegar com uma leitura automática ainda presa em voo, e se o "Atualizar"
  // respeitasse o guard o clique não dispararia nada até ela resolver (8s) — o botão
  // pareceria funcionar sem fazer nada. É justamente com uma leitura presa que o clique
  // mais precisa sair. Risco aceito: a leitura presa pode sobrescrever a manual por um
  // ciclo; o tick seguinte (3s) relê e corrige.
  lerPagamento({ idPagamento = null, manual = false } = {}) {
    if (!idPagamento) return
    this.descartarResiduoDeOutroPagamento(idPagamento)
    if (store.getters[this.KEY_PAGAMENTO_LOADING] && !manual) return
    store.dispatch(this.ACTION_SET_PAGAMENTO_LOADING, true)
    const soltar = () => store.dispatch(this.ACTION_SET_PAGAMENTO_LOADING, false)
    return this.lerPagamentoNoServidor(idPagamento).finally(soltar)
  }

  async lerPagamentoNoServidor(idPagamento) {
    const r =
      (await con.get(
        CREDITOS_PAGAMENTO(idPagamento), DATA_VAZIO, TIPO_GET, NAO_IGNORAR_ERRO, COM_PRAZO_PAGAMENTO
      )) || this.buildErrorResponse({ message: ERRO_PAGAMENTO, value: null })
    this.aplicarPagamentoLido(r, idPagamento)
    return r
  }

  // GET /creditos/extrato — lançamentos do período, TODAS as naturezas (spec-003, [US-4]),
  // persistidos em `extrato*`. `{ filters, page }` é a MESMA forma do `fetchRuns` do slice
  // irmão. Filtros e página são gravados ANTES do request e o pedido lê o estado APLICADO:
  // é o que faz `carregarExtrato()` sem argumento repetir a última consulta (o "tentar de
  // novo"), e paginar com `{ page }` manter o recorte já aplicado.
  //
  // ★ O envelope atravessa INTACTO: `creditos` chega como STRING de 6 casas e o sinal é do
  // DADO (`-0.433000` é saída) — a API não o deriva da natureza nem da aba da tela.
  // ★ Trocar o filtro com a carga em voo é o gesto mais comum da tela: o pedido novo SAI
  // com o recorte pedido, e a resposta obsoleta não entra sob o rótulo errado.
  async carregarExtrato({ filters, page } = {}) {
    if (filters) store.dispatch(this.ACTION_SET_EXTRATO_FILTERS, filters)
    if (page) store.dispatch(this.ACTION_SET_EXTRATO_PAGE, page)

    const seq = ++this.extratoSeq
    store.dispatch(this.ACTION_SET_EXTRATO_LOADING, true)

    try {
      const r =
        (await con.get(CREDITOS_EXTRATO + formatQueryParams(this.paramsDoExtrato()))) ||
        this.buildErrorResponse({ message: ERRO_EXTRATO, value: null })
      if (seq === this.extratoSeq) this.aplicarExtratoLido(r)
      return r
    } finally {
      if (seq === this.extratoSeq) store.dispatch(this.ACTION_SET_EXTRATO_LOADING, false)
    }
  }

  // GET /creditos/resumo — os dois indicadores do período ([US-2], [US-3]), persistidos em
  // `resumo*`. A agregação é feita no BANCO, sobre o mesmo recorte do extrato. Quem garante
  // "um período para a tela inteira" é a VIEW, pedindo o mesmo `de`/`ate` às duas leituras:
  // encadear uma na outra aqui esconderia a origem da segunda requisição.
  async carregarResumo({ filters } = {}) {
    if (filters) store.dispatch(this.ACTION_SET_RESUMO_FILTERS, filters)

    const seq = ++this.resumoSeq
    store.dispatch(this.ACTION_SET_RESUMO_LOADING, true)

    try {
      const r =
        (await con.get(CREDITOS_RESUMO + formatQueryParams(this.paramsDoResumo()))) ||
        this.buildErrorResponse({ message: ERRO_RESUMO, value: null })
      if (seq === this.resumoSeq) this.aplicarResumoLido(r)
      return r
    } finally {
      if (seq === this.resumoSeq) store.dispatch(this.ACTION_SET_RESUMO_LOADING, false)
    }
  }

  // ---------- HELPERS ----------

  // ★ O saldo anterior é DESCARTADO em falha: manter o número antigo faria a tela afirmar
  // um valor que o backend não confirmou neste ciclo, indistinguível de saldo atual.
  aplicarSaldoLido(r) {
    if (r.status === STATUS_OK) {
      store.dispatch(this.ACTION_SET_SALDO, r.value || null)
      store.dispatch(this.ACTION_SET_SALDO_FORBIDDEN, false)
      return
    }
    store.dispatch(this.ACTION_SET_SALDO, null)
    store.dispatch(this.ACTION_SET_SALDO_FORBIDDEN, r.status === STATUS_FORBIDDEN)
    if (r.status !== STATUS_FORBIDDEN) store.dispatch(this.ACTION_SNACK, r)
  }

  // Catálogo anterior descartado em falha pelo mesmo motivo do saldo. Sem snack — a falha
  // TEM estado de tela próprio, com "tentar de novo" (US-1). `pacotes: []` é catálogo
  // vazio e é preservado: cair para uma lista hardcoded seria vender preço que o servidor
  // não confirma.
  aplicarPacotesLido(r) {
    if (r.status === STATUS_OK) {
      store.dispatch(this.ACTION_SET_PACOTES, r.value || null)
      store.dispatch(this.ACTION_SET_PACOTES_ERRO, null)
      return
    }
    store.dispatch(this.ACTION_SET_PACOTES, null)
    store.dispatch(this.ACTION_SET_PACOTES_ERRO, r.message || ERRO_PACOTES_SEM_MENSAGEM)
  }

  // Só um checkout BEM-SUCEDIDO entra no slice: guardar o payload do 502 faria a view
  // navegar para uma `url_checkout` inexistente. O `id_pagamento` do 502 é o protocolo do
  // suporte (o pagamento existe em `falhou`) e vai para `checkoutErro`.
  aplicarCheckoutCriado(r) {
    if (r.status === STATUS_OK) {
      store.dispatch(this.ACTION_SET_CHECKOUT, r.value || null)
      store.dispatch(this.ACTION_SET_CHECKOUT_ERRO, null)
      return
    }
    store.dispatch(this.ACTION_SET_CHECKOUT, null)
    store.dispatch(this.ACTION_SET_CHECKOUT_ERRO, {
      message: r.message || '',
      idPagamento: (r.value && r.value.id_pagamento) || null,
    })
    store.dispatch(this.ACTION_SNACK, r)
  }

  // ★ Divergência DELIBERADA da regra do saldo: aqui o último estado confirmado é
  // PRESERVADO num erro transitório — um 500 no meio do polling não pode apagar
  // "Processando" da tela, e o polling segue.
  aplicarPagamentoLido(r, idPagamento) {
    if (r.status === STATUS_OK) {
      store.dispatch(this.ACTION_SET_PAGAMENTO, r.value || null)
      store.dispatch(this.ACTION_SET_PAGAMENTO_NAO_ENCONTRADO, false)
      this.relerSaldoSeCreditado(r.value, idPagamento)
      return
    }
    if (r.status === STATUS_NOT_FOUND) {
      store.dispatch(this.ACTION_SET_PAGAMENTO, null)
      store.dispatch(this.ACTION_SET_PAGAMENTO_NAO_ENCONTRADO, true)
      return
    }
    store.dispatch(this.ACTION_SNACK, r)
  }

  // ★ `creditado` (e não o rótulo PT-BR) diz que os lançamentos existem. Quando vira true, o
  // saldo é RELIDO DO BACKEND — o cliente nunca soma os créditos do pacote no número da
  // tela. `saldoRecarregadoPara` evita uma releitura por poll sobre um número que parou.
  relerSaldoSeCreditado(pagamento, idPagamento) {
    const creditado = Boolean(pagamento && pagamento.creditado)
    if (!creditado || store.getters[this.KEY_SALDO_RECARREGADO_PARA] === idPagamento) return
    store.dispatch(this.ACTION_SET_SALDO_RECARREGADO_PARA, idPagamento)
    this.carregarSaldo()
  }

  // Outro id: o que está no store é resíduo da navegação anterior — inclusive um
  // `pagamentoNaoEncontrado` de um 404 antigo, que faria a tela nem ligar o polling deste
  // pagamento. Mesmo id não é tocado (sem piscar no poll).
  descartarResiduoDeOutroPagamento(idPagamento) {
    const noStore = store.getters[this.KEY_PAGAMENTO]
    const idNoStore = noStore && noStore.id_pagamento
    if (idPagamento === idNoStore) return
    store.dispatch(this.ACTION_SET_PAGAMENTO, null)
    store.dispatch(this.ACTION_SET_PAGAMENTO_NAO_ENCONTRADO, false)
  }

  paramsDoExtrato() {
    const filtros = store.getters[this.KEY_EXTRATO_FILTERS] || {}
    const pagina = store.getters[this.KEY_EXTRATO_PAGE] || {}
    return {
      de: filtros.de,
      ate: filtros.ate,
      natureza: filtros.natureza,
      id_profissional: filtros.idProfissional,
      limit: pagina.limit,
      offset: pagina.offset,
    }
  }

  paramsDoResumo() {
    const filtros = store.getters[this.KEY_RESUMO_FILTERS] || {}
    return { de: filtros.de, ate: filtros.ate, id_profissional: filtros.idProfissional }
  }

  // ★ Os três desfechos são DISTINTOS na tela e por isso no state: 200 ⇒ dados (lista vazia
  // é "período sem movimento", com `erro` limpo); 403 ⇒ `forbidden`; demais ⇒ `erro` com o
  // texto do backend. Fundir "vazio" com "falhou" diria "nenhum lançamento" para uma
  // requisição que não voltou. Dado não confirmado neste ciclo não fica na tela.
  aplicarExtratoLido(r) {
    if (r.status === STATUS_OK) {
      const value = r.value || {}
      store.dispatch(this.ACTION_SET_EXTRATO, value.lancamentos || [])
      store.dispatch(this.ACTION_SET_EXTRATO_TOTAL, value.total || 0)
      // Backend anterior à spec-006 não manda os três campos: `undefined` cai nos defaults
      // da mutation e a tela simplesmente não exibe aviso — nunca um erro.
      store.dispatch(this.ACTION_SET_EXTRATO_REGUA, {
        mudou: value.regua_mudou_no_periodo,
        reguas: value.reguas_no_periodo,
        marco: value.marco_regua,
      })
      store.dispatch(this.ACTION_SET_EXTRATO_FORBIDDEN, false)
      store.dispatch(this.ACTION_SET_EXTRATO_ERRO, null)
      return
    }
    store.dispatch(this.ACTION_SET_EXTRATO, [])
    store.dispatch(this.ACTION_SET_EXTRATO_TOTAL, 0)
    store.dispatch(this.ACTION_SET_EXTRATO_REGUA, null)
    const negado = r.status === STATUS_FORBIDDEN
    store.dispatch(this.ACTION_SET_EXTRATO_FORBIDDEN, negado)
    store.dispatch(
      this.ACTION_SET_EXTRATO_ERRO, negado ? null : r.message || ERRO_EXTRATO_SEM_MENSAGEM
    )
  }

  // ★ Em QUALQUER falha o resumo volta a `null`: sem número na tela é a única leitura
  // honesta de uma leitura que não voltou. Zerar afirmaria "você não consumiu nada" — e
  // zero vindo do SERVIDOR é dado legítimo, que o ramo de sucesso continua exibindo.
  aplicarResumoLido(r) {
    if (r.status === STATUS_OK) {
      store.dispatch(this.ACTION_SET_RESUMO, r.value || null)
      store.dispatch(this.ACTION_SET_RESUMO_FORBIDDEN, false)
      store.dispatch(this.ACTION_SET_RESUMO_ERRO, null)
      return
    }
    store.dispatch(this.ACTION_SET_RESUMO, null)
    const negado = r.status === STATUS_FORBIDDEN
    store.dispatch(this.ACTION_SET_RESUMO_FORBIDDEN, negado)
    store.dispatch(
      this.ACTION_SET_RESUMO_ERRO, negado ? null : r.message || ERRO_RESUMO_SEM_MENSAGEM
    )
  }
}

// Singleton no padrão do módulo: instância nomeada + export explícito (a classe não é
// exportada — export sem consumidor é código morto que o próximo leitor tenta preservar).
const apiAiFrameworkCreditos = new APIAiFrameworkCreditos()

export { apiAiFrameworkCreditos }
